/**
 * In-app browser (embedded webview) a page is running inside, or `none`.
 *
 * @example
 * const app: InAppBrowser = detectInAppBrowser(navigator.userAgent);
 */
export type InAppBrowser =
  | "facebook"
  | "messenger"
  | "instagram"
  | "tiktok"
  | "snapchat"
  | "whatsapp"
  | "telegram"
  | "twitter"
  | "line"
  | "googleApp"
  | "androidWebView"
  | "iosWebView"
  | "none";

// Ordered: specific apps first, generic webview markers last.
const IN_APP_BROWSER_PATTERNS: ReadonlyArray<[InAppBrowser, RegExp]> = [
  ["messenger", /FB_IAB\/MESSENGER|FBAN\/Messenger|MessengerForiOS/i],
  ["facebook", /FBAN|FBAV|FB_IAB|FBIOS|FB4A/i],
  ["instagram", /Instagram/i],
  ["tiktok", /musical_ly|BytedanceWebview|TikTok|trill_/i],
  ["snapchat", /Snapchat/i],
  ["whatsapp", /WhatsApp/i],
  ["telegram", /Telegram/i],
  ["twitter", /Twitter for|TwitterAndroid/i],
  ["line", /\bLine\//],
  ["googleApp", /\bGSA\//],
  ["androidWebView", /; wv\)/],
];

/**
 * Identify an embedded in-app browser from its user agent.
 *
 * @remarks
 * Detection is best-effort: it only changes which help text a student sees
 * and is reported in telemetry; it never changes the security checks.
 *
 * @example
 * detectInAppBrowser("Mozilla/5.0 (Linux; Android 13; wv) ... FBAV/440.0"); // "facebook"
 */
export function detectInAppBrowser(userAgent: string | undefined): InAppBrowser {
  if (!userAgent) return "none";

  for (const [name, pattern] of IN_APP_BROWSER_PATTERNS) {
    if (pattern.test(userAgent)) return name;
  }

  // iOS webviews (WKWebView inside an app) omit the "Safari/" token.
  if (/iPhone|iPad|iPod/i.test(userAgent) && !/Safari\//i.test(userAgent)) {
    return "iosWebView";
  }

  return "none";
}

/**
 * Whether the user agent belongs to an Android device.
 *
 * @example
 * isAndroidUserAgent(navigator.userAgent);
 */
export function isAndroidUserAgent(userAgent: string | undefined): boolean {
  return Boolean(userAgent && /Android/i.test(userAgent));
}

/**
 * Build an Android intent URL that opens the page in Chrome.
 *
 * @remarks
 * Only `https` pages are offered; when Chrome is missing Android follows the
 * fallback URL in the default browser.
 *
 * @example
 * buildChromeIntentUrl("https://teacher.example/auth/login");
 * // "intent://teacher.example/auth/login#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=...;end"
 */
export function buildChromeIntentUrl(pageUrl: string): string | undefined {
  let url: URL;

  try {
    url = new URL(pageUrl);
  } catch {
    return undefined;
  }

  url.protocol = "https:";
  url.hash = "";

  const target = `${url.host}${url.pathname}${url.search}`;

  return `intent://${target}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(
    url.toString(),
  )};end`;
}

/**
 * Whether a page was loaded over plain HTTP on a public host.
 *
 * @remarks
 * WebCrypto (`crypto.subtle`) and `Secure` cookies are both disabled on
 * insecure origins, so such a page can never complete a trusted-device login.
 * Loopback and LAN development hosts are excluded.
 *
 * @example
 * isInsecurePublicPage({ protocol: "http:", hostname: "teacher.mentoor.io" }); // true
 */
export function isInsecurePublicPage(
  location: Pick<Location, "protocol" | "hostname"> | undefined,
): boolean {
  if (!location || location.protocol !== "http:") return false;

  const hostname = location.hostname.toLowerCase();

  if (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".local") ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) ||
    hostname.includes(":")
  ) {
    return false;
  }

  return true;
}

/**
 * The HTTPS address of the current page.
 *
 * @example
 * toSecurePageUrl("http://teacher.example/auth/login?x=1"); // "https://teacher.example/auth/login?x=1"
 */
export function toSecurePageUrl(pageUrl: string): string | undefined {
  try {
    const url = new URL(pageUrl);

    url.protocol = "https:";
    url.port = "";

    return url.toString();
  } catch {
    return undefined;
  }
}

const HTTPS_PROBE_TIMEOUT_MS = 4_000;

/**
 * Move a page served over plain HTTP to HTTPS once HTTPS is proven to work.
 *
 * @remarks
 * Some tenant hosts still answer on plain HTTP without redirecting, and on
 * HTTP the browser hides `crypto.subtle`, so students could never sign in.
 * The probe makes sure a host without a certificate is not redirected into
 * a dead page.
 *
 * @example
 * void upgradeInsecurePageToHttps(window);
 */
export async function upgradeInsecurePageToHttps(
  browserWindow: Pick<Window, "location" | "fetch"> | undefined,
): Promise<boolean> {
  if (!browserWindow || !isInsecurePublicPage(browserWindow.location)) {
    return false;
  }

  const secureUrl = toSecurePageUrl(browserWindow.location.href);

  if (!secureUrl) return false;

  const abortController = new AbortController();
  const timeoutId = setTimeout(
    () => abortController.abort(),
    HTTPS_PROBE_TIMEOUT_MS,
  );

  try {
    await browserWindow.fetch(new URL("/favicon.ico", secureUrl).toString(), {
      cache: "no-store",
      mode: "no-cors",
      signal: abortController.signal,
    });

    browserWindow.location.replace(secureUrl);

    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
